import { useEffect, useCallback, useState } from "react";
import { Mic, Square, MicOff, ShieldAlert, Settings, Keyboard, AlertTriangle, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranscribeAudio } from "@workspace/api-client-react";
import { ApiError } from "@workspace/api-client-react/custom-fetch";
import { useAppStore } from "@/store";
import { useAudioRecorder } from "@/hooks/use-audio-recorder";
import { cn } from "@/lib/utils";
import { TranscriptionProgress } from "@/components/transcription-progress";

function getApiErrorDetails(err: Error | null | undefined): string | undefined {
  if (err instanceof ApiError && err.data !== null && typeof err.data === "object") {
    const details = (err.data as Record<string, unknown>).details;
    if (typeof details === "string" && details.trim()) return details;
  }
  return undefined;
}

export function VoiceRecorder({ onTranscriptionComplete, textareaRef }: { onTranscriptionComplete?: (text: string) => void; textareaRef?: React.RefObject<HTMLTextAreaElement | null> }) {
  const { sessionId, setIdea } = useAppStore();
  const [showErrorDetail, setShowErrorDetail] = useState(false);

  const scrollToTextarea = useCallback(() => {
    if (textareaRef?.current) {
      textareaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      textareaRef.current.focus();
    }
  }, [textareaRef]);

  const transcribeMutation = useTranscribeAudio({
    mutation: {
      onSuccess: (data) => {
        setIdea(data.text);
        if (onTranscriptionComplete) onTranscriptionComplete(data.text);
      },
    },
    request: { headers: { "x-session-id": sessionId } },
  });

  const recorder = useAudioRecorder({
    onRecordingComplete: (blob) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        if (!reader.result || typeof reader.result !== "string") return;
        const base64String = reader.result.split(",")[1];
        if (!base64String) return;
        transcribeMutation.mutate({ data: { audioBase64: base64String, sessionId } });
      };
      reader.onerror = () => {
        transcribeMutation.reset();
      };
    },
  });

  useEffect(() => {
    if (transcribeMutation.isSuccess) {
      scrollToTextarea();
    }
  }, [transcribeMutation.isSuccess, scrollToTextarea]);

  if (recorder.micPermission === "unavailable") {
    return (
      <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-2xl bg-card/30">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <MicOff className="w-7 h-7 text-destructive" />
          </div>
          <div>
            <p className="text-lg font-semibold text-foreground">Microphone Not Available</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">
              Your browser doesn't support microphone access. Try using Chrome, Firefox, or Edge.
            </p>
          </div>
          <button
            onClick={scrollToTextarea}
            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors cursor-pointer"
          >
            <Keyboard className="w-4 h-4" />
            <span className="underline">Type your idea instead</span>
          </button>
        </div>
      </div>
    );
  }

  if (recorder.micPermission === "denied") {
    return (
      <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-amber-500/40 rounded-2xl bg-amber-500/5">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Microphone Access Blocked</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm">
              You previously blocked microphone access. To re-enable it:
            </p>
          </div>
          <div className="text-left bg-card/50 rounded-xl p-4 border border-border w-full max-w-sm space-y-2">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
              <p className="text-sm text-foreground">Click the <Settings className="w-3.5 h-3.5 inline" /> lock/settings icon in your browser's address bar</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
              <p className="text-sm text-foreground">Find "Microphone" and change it to "Allow"</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
              <p className="text-sm text-foreground">Click the button below to try again</p>
            </div>
          </div>
          <button
            onClick={recorder.recheckPermission}
            className="px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            I've enabled it — try again
          </button>
          <button
            onClick={scrollToTextarea}
            className="flex items-center gap-2 text-xs text-primary hover:text-primary/80 transition-colors cursor-pointer"
          >
            <Keyboard className="w-3.5 h-3.5" />
            <span className="underline">Type your idea instead</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-2xl bg-card/30 transition-all hover:border-primary/50 group relative overflow-hidden">
      <AnimatePresence>
        {recorder.isRecording && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-destructive/5 blur-3xl pointer-events-none"
          />
        )}
      </AnimatePresence>

      <div className="relative z-10 flex flex-col items-center">
        {recorder.deviceError ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-3 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-400" />
            </div>
            <p className="text-sm font-medium text-foreground">{recorder.deviceError}</p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  recorder.clearDeviceError();
                  recorder.startRecording();
                }}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => {
                  recorder.clearDeviceError();
                  scrollToTextarea();
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm text-primary hover:text-primary/80 transition-colors"
              >
                <Keyboard className="w-3.5 h-3.5" />
                <span className="underline">Type instead</span>
              </button>
            </div>
          </motion.div>
        ) : transcribeMutation.isPending ? (
          <TranscriptionProgress isActive variant="transcription" />
        ) : transcribeMutation.isSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-3"
          >
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }}>
                <Mic className="w-7 h-7 text-emerald-400" />
              </motion.div>
            </div>
            <p className="text-sm font-medium text-emerald-400">Idea captured! Edit below or generate your post.</p>
            <button
              onClick={() => transcribeMutation.reset()}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
            >
              Record another
            </button>
          </motion.div>
        ) : transcribeMutation.isError ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <MicOff className="w-7 h-7 text-destructive" />
            </div>
            {(() => {
              const err = transcribeMutation.error;
              const rawMsg = err?.message ?? "";
              const mainError = rawMsg.replace(/^HTTP \d{3}[^:]*:\s*/, "") || "Transcription failed. Please try again.";
              const detailText = getApiErrorDetails(err) ?? (rawMsg.length > 80 ? rawMsg : undefined);
              return (
                <div className="flex flex-col items-center gap-2 text-center max-w-xs">
                  <p className="text-sm font-medium text-destructive">{mainError}</p>
                  {detailText && (
                    <button
                      onClick={() => setShowErrorDetail((v) => !v)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ChevronDown className={cn("w-3 h-3 transition-transform", showErrorDetail && "rotate-180")} />
                      {showErrorDetail ? "Hide details" : "What went wrong?"}
                    </button>
                  )}
                  {showErrorDetail && detailText && (
                    <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 max-w-full break-words">
                      {detailText}
                    </p>
                  )}
                </div>
              );
            })()}
            <button
              onClick={() => { transcribeMutation.reset(); setShowErrorDetail(false); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={recorder.isRecording ? recorder.stopRecording : recorder.startRecording}
              aria-label={recorder.isRecording ? "Stop recording" : "Start recording"}
              className={cn(
                "w-20 h-20 rounded-full flex items-center justify-center shadow-xl transition-colors duration-300 relative",
                recorder.isRecording
                  ? "bg-destructive text-destructive-foreground shadow-destructive/30"
                  : "bg-primary text-primary-foreground shadow-primary/30 hover:bg-primary/90"
              )}
            >
              {recorder.isRecording ? <Square className="w-8 h-8 fill-current" /> : <Mic className="w-8 h-8" />}
              {recorder.isRecording && (
                <>
                  <span className="absolute w-full h-full rounded-full border-2 border-destructive animate-ping opacity-75" style={{ animationDuration: "1.5s" }} />
                  <span className="absolute w-full h-full rounded-full border-2 border-destructive animate-ping opacity-50" style={{ animationDuration: "2s" }} />
                </>
              )}
            </motion.button>

            <div className="mt-6 text-center">
              {recorder.isRecording ? (
                <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                  <span className="text-2xl font-display font-medium tabular-nums tracking-tight">
                    {recorder.formatTime(recorder.recordingTime)}
                  </span>
                </motion.div>
              ) : (
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Record a thought</h3>
                  <p className="text-sm text-muted-foreground mt-1">Dump your raw ideas, we'll format them.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
